import type { Constant } from '@pbt/pbt-ui-components'
import { Utils } from '@pbt/pbt-ui-components'

import {
  DeclineReasonToOrderDisplayStatus,
  OrderDisplayStatus,
} from '~/constants/orderStatusChip'
import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import type { PrescriptionDeclineReasonType } from '~/constants/PrescriptionWorkflow'
import type { Prescription } from '~/types'
import {
  getPrescriptionType,
  getPrescriptionWorkflowType,
} from '~/utils/prescription'

export interface OrderStatusInput {
  prescription: Prescription
  prescriptionStates?: Constant[]
  retailOrderStates?: Constant[]
}

const getDeclinedOrderDisplayStatus = (
  rejectionReason?: string | null,
): OrderDisplayStatus | null => {
  if (!rejectionReason) {
    return OrderDisplayStatus.DECLINED_OTHER
  }

  const rejectionReasonLower = rejectionReason.toLowerCase()
  const matchedEntry = Object.entries(DeclineReasonToOrderDisplayStatus).find(
    ([reasonType]) => {
      const reasonTypeValue = reasonType as PrescriptionDeclineReasonType
      const reasonTypeString = reasonTypeValue.toLowerCase()
      return rejectionReasonLower.includes(reasonTypeString)
    },
  )

  return matchedEntry ? matchedEntry[1] : OrderDisplayStatus.DECLINED_OTHER
}

export const getOrderDisplayStatus = (
  input: OrderStatusInput,
): OrderDisplayStatus | null => {
  const { prescription, prescriptionStates, retailOrderStates } = input

  const { isChewyActiveRx, isChewyReactiveRx } = getPrescriptionWorkflowType({
    orderType: prescription.type,
    prescriptionType: prescription.prescriptionType,
    origin: prescription.origin,
  })

  const isChewy = isChewyActiveRx || isChewyReactiveRx
  const { isInHouse, isScriptOut } = getPrescriptionType(
    prescription.prescriptionType,
  )

  if (
    isScriptOut ||
    prescription.prescriptionType === PrescriptionType.SCRIPT_OUT
  ) {
    return null
  }

  const stateName = prescription.stateId
    ? Utils.getConstantName(prescription.stateId, prescriptionStates ?? [])
    : null

  if (!stateName) {
    return null
  }

  if (isChewy) {
    if (stateName === PrescriptionStates.CANCELED) {
      return OrderDisplayStatus.CANCELED
    }

    if (stateName === PrescriptionStates.DECLINED) {
      const declinedStatus = getDeclinedOrderDisplayStatus(
        prescription.rejectionReason,
      )
      if (declinedStatus) {
        return declinedStatus
      }
    }

    if (prescription.lineItem?.orderStateId && retailOrderStates) {
      const orderStateName = Utils.getConstantName(
        prescription.lineItem.orderStateId,
        retailOrderStates,
      )

      const orderStateToDisplayStatus: Record<string, OrderDisplayStatus> = {
        Approved: OrderDisplayStatus.APPROVED,
        Shipped: OrderDisplayStatus.SHIPPED,
        Delivered: OrderDisplayStatus.DELIVERED,
        'Pending pharmacist review':
          OrderDisplayStatus.PENDING_PHARMACIST_REVIEW,
        'Pharmacist declined': OrderDisplayStatus.PHARMACIST_DECLINED,
      }

      const orderStatus = orderStateToDisplayStatus[orderStateName]
      if (orderStatus) {
        return orderStatus
      }
    }

    if (stateName === PrescriptionStates.APPROVED) {
      return OrderDisplayStatus.APPROVED
    }
  }

  if (isInHouse) {
    if (prescription.lineItem?.orderStateId && retailOrderStates) {
      const orderStateName = Utils.getConstantName(
        prescription.lineItem.orderStateId,
        retailOrderStates,
      )

      if (orderStateName === 'Shipped') {
        return OrderDisplayStatus.SHIPPED
      }
    }

    if (stateName === PrescriptionStates.APPROVED) {
      return OrderDisplayStatus.APPROVED
    }
  }

  return null
}
